import React, { useState } from "react";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  BrainCircuit, Plus, Play, Pause, Sparkles, Loader2,
  ShieldAlert, TrendingUp, AlertTriangle, Info, ArrowRight, Wand2,
  Settings2, Radio, Activity, ChevronRight, Rss, GitBranch,
} from "lucide-react";
import { DETECTION_WORKFLOWS, AI_INSIGHTS, EVENT_TYPES, formatEventType, DETECTED_SIGNALS, AI_USECASES } from "@/data/mockData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ExpressionBuilder from "@/components/ExpressionBuilder";
import NodeConfigDialog from "@/components/workflow/NodeConfigDialog";
import DataTable from "@/components/DataTable";

const SEV_STYLES = {
  high: { color: "text-red-500 border-red-500/30 bg-red-500/10", icon: ShieldAlert },
  medium: { color: "text-amber-500 border-amber-500/30 bg-amber-500/10", icon: AlertTriangle },
  low: { color: "text-primary border-primary/30 bg-primary/10", icon: Info },
};

export default function DetectionEngine() {
  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div>
        <div className="label-caps mb-2">Signals · always-on</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Detection Engine</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-xl">
          Manually authored event sequences plus AI listeners deployed server-side.
          Build detections yourself, or review what the platform team already provisioned.
        </p>
      </div>

      <Tabs defaultValue="workflows" className="w-full">
        <TabsList data-testid="detection-tabs" className="bg-secondary/60 h-11">
          <TabsTrigger value="workflows" data-testid="tab-detection-workflows" className="gap-2 data-[state=active]:bg-card">
            <GitBranch className="w-3.5 h-3.5" /> Detection Workflows
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai-detection" className="gap-2 data-[state=active]:bg-card">
            <BrainCircuit className="w-3.5 h-3.5 text-primary" /> AI Detection
            <span className="ml-1 text-[0.6rem] px-1.5 py-0.5 bg-primary/20 text-primary rounded">LIVE</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="mt-6">
          <DetectionWorkflowsTab />
        </TabsContent>

        <TabsContent value="ai" className="mt-6">
          <AIDetectionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ================= Detection Workflows Tab ================= */

function DetectionWorkflowsTab() {
  const [detections, setDetections] = useState(() =>
    DETECTION_WORKFLOWS.map((d) => ({
      ...d,
      steps: d.steps.map((s) =>
        s.op === "action"
          ? {
              ...s,
              webhook: {
                method: "POST",
                url: "https://compliance.optikurve.io/action",
                headers: [{ k: "Content-Type", v: "application/json" }],
                params: [],
                body: `{\n  "customer": "{{ event.customer_code }}",\n  "detection": "${d.id}",\n  "action": "${s.action}"\n}`,
                auth_type: "bearer",
                auth_token: "{{ secret.compliance_token }}",
                timeout: 5000,
                retry: "linear_3",
                body_type: "json",
              },
            }
          : s
      ),
    }))
  );
  const [editing, setEditing] = useState(null);

  const toggle = (id) => {
    setDetections((ds) => ds.map((d) => (d.id === id ? { ...d, status: d.status === "active" ? "disabled" : "active" } : d)));
    toast.success("Detection status updated");
  };

  const createNew = () => {
    const blank = {
      id: `det_${Date.now()}`,
      name: "Untitled Detection",
      description: "Describe what pattern to detect",
      status: "disabled",
      matches_today: 0,
      steps: [
        { id: `s${Date.now()}`, op: "event_exists", event: "credit_card_requested", within: "1 hour", conditionExpr: "" },
      ],
    };
    setDetections((d) => [blank, ...d]);
    setEditing(blank);
  };

  const save = (updated) => {
    setDetections((ds) => ds.map((d) => (d.id === updated.id ? updated : d)));
    setEditing(null);
    toast.success(`"${updated.name}" saved`);
  };

  if (editing) return <DetectionEditor detection={editing} onSave={save} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {detections.length} detection rules — {detections.filter((d) => d.status === "active").length} running
        </div>
        <Button onClick={createNew} data-testid="detection-create" className="gap-2 bg-primary hover:bg-[#0B2B4E]">
          <Plus className="w-4 h-4" /> New detection
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {detections.map((d) => (
          <Card key={d.id} data-testid={`detection-card-${d.id}`} className="p-5 border-border hover:border-primary/50 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div>
                <Badge variant="outline" className={d.status === "active" ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" : "border-muted-foreground/30 text-muted-foreground"}>
                  {d.status === "active" ? <><Play className="w-2.5 h-2.5 mr-1" /> Live</> : <><Pause className="w-2.5 h-2.5 mr-1" /> Paused</>}
                </Badge>
                <div className="font-display text-xl font-semibold mt-2">{d.name}</div>
                <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.description}</div>
              </div>
              <Switch checked={d.status === "active"} onCheckedChange={() => toggle(d.id)} data-testid={`detection-toggle-${d.id}`} />
            </div>

            <div className="my-4 space-y-1.5">
              {d.steps.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 text-xs font-mono">
                  <div className="w-5 h-5 rounded-sm bg-secondary text-muted-foreground flex items-center justify-center shrink-0 text-[0.65rem]">{i + 1}</div>
                  <StepBadge op={s.op} />
                  {s.event && <span className="text-foreground truncate">{formatEventType(s.event)}</span>}
                  {s.within && <span className="text-muted-foreground">within {s.within}</span>}
                  {s.count && <span className="text-muted-foreground">count {s.count}</span>}
                  {s.action && <span className="text-primary">→ {s.action}</span>}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs border-t border-border pt-3">
              <span className="text-muted-foreground">Matches today: <span className="font-mono text-foreground">{d.matches_today}</span></span>
              <Button variant="ghost" size="sm" onClick={() => setEditing(d)} data-testid={`detection-edit-${d.id}`} className="h-7 hover:text-primary">
                Edit sequence →
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Detected signals data table */}
      <div className="pt-4">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="label-caps">Signals · fired detections</div>
            <div className="font-display text-2xl font-semibold">Detected signals feed</div>
          </div>
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 bg-emerald-500/10 gap-1.5">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-500/60 animate-ping" />
              <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-500" />
            </span>
            Live
          </Badge>
        </div>
        <DataTable
          testIdPrefix="signals-table"
          initialPageSize={10}
          columns={[
            { key: "id", header: "Signal ID", render: (r) => <span className="font-mono text-xs">{r.id}</span> },
            { key: "detection", header: "Detection", render: (r) => <span className="font-medium text-sm">{r.detection}</span> },
            { key: "customer", header: "Customer", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.customer}</span> },
            { key: "event", header: "Trigger event", render: (r) => <span className="text-xs">{formatEventType(r.event)}</span> },
            { key: "detected_at", header: "Detected at", render: (r) => <span className="text-xs text-muted-foreground">{new Date(r.detected_at).toLocaleString()}</span> },
            { key: "action_taken", header: "Action", render: (r) => <code className="font-mono text-[0.7rem] text-primary">{r.action_taken}</code> },
            { key: "status", header: "Status", render: (r) => (
              <Badge variant="outline" className={
                r.status === "actioned" ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" :
                r.status === "review" ? "border-[#E9B44C]/60 text-[#B67F1B] dark:text-[#E9B44C] bg-[#E9B44C]/10" :
                "border-muted-foreground/30 text-muted-foreground"
              }>{r.status}</Badge>
            )},
          ]}
          data={DETECTED_SIGNALS}
          searchable={["id", "detection", "customer", "event", "action_taken", "status"]}
        />
      </div>
    </div>
  );
}

function StepBadge({ op }) {
  const map = {
    event_exists: { label: "exists", color: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" },
    event_absent: { label: "absent", color: "text-red-500 border-red-500/40 bg-red-500/10" },
    event_count: { label: "count", color: "text-amber-500 border-amber-500/40 bg-amber-500/10" },
    action: { label: "action", color: "text-primary border-primary/40 bg-primary/10" },
  };
  const m = map[op] || map.event_exists;
  return <span className={cn("px-1.5 py-0.5 rounded-sm border text-[0.65rem] uppercase tracking-wider", m.color)}>{m.label}</span>;
}

function DetectionEditor({ detection, onSave, onCancel }) {
  const [name, setName] = useState(detection.name);
  const [description, setDescription] = useState(detection.description);
  const [enabled, setEnabled] = useState(detection.status === "active");
  const [steps, setSteps] = useState(detection.steps);
  const [webhookStepId, setWebhookStepId] = useState(null);

  const addStep = (op) => {
    const id = `s${Date.now()}`;
    if (op === "action") {
      setSteps((s) => [...s, { id, op: "action", action: "notify_compliance", target: "customer", webhook: {
        method: "POST", url: "https://compliance.optikurve.io/action",
        headers: [{ k: "Content-Type", v: "application/json" }],
        params: [], body: `{\n  "customer": "{{ event.customer_code }}"\n}`,
        auth_type: "bearer", auth_token: "{{ secret.token }}",
        timeout: 5000, retry: "linear_3", body_type: "json",
      }}]);
    } else {
      setSteps((s) => [...s, {
        id, op, event: "credit_card_transaction", within: "1 hour",
        conditionExpr: op === "event_count" ? "" : "{{ event.payload.amount > 100 }}",
        count: op === "event_count" ? ">= 3" : undefined,
      }]);
    }
  };

  const removeStep = (id) => setSteps((s) => s.filter((x) => x.id !== id));
  const updateStep = (id, patch) => setSteps((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const webhookStep = steps.find((s) => s.id === webhookStepId);
  const webhookNode = webhookStep
    ? { type: "detectionAction", data: { label: webhookStep.action, ...(webhookStep.webhook || {}) } }
    : null;

  return (
    <Card className="border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <div className="label-caps">Detection · sequence builder</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="detection-editor-name"
            className="bg-transparent font-display text-2xl font-semibold outline-none w-full mt-1"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="label-caps">Enabled</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="detection-editor-enabled" />
          </div>
          <Button variant="outline" onClick={onCancel} data-testid="detection-editor-cancel">Cancel</Button>
          <Button
            data-testid="detection-editor-save"
            onClick={() => onSave({ ...detection, name, description, status: enabled ? "active" : "disabled", steps })}
            className="bg-primary hover:bg-[#0B2B4E]"
          >Save</Button>
        </div>
      </div>

      <div className="space-y-1.5 mb-6">
        <Label className="label-caps">Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="detection-editor-desc" />
      </div>

      <div className="label-caps mb-3">Sequence steps</div>
      <div className="space-y-3">
        {steps.map((step, idx) => (
          <div key={step.id} className="p-4 border border-border rounded-md bg-secondary/40" data-testid={`step-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-sm bg-primary/10 text-primary flex items-center justify-center text-xs font-mono">{idx + 1}</div>
                <Select value={step.op} onValueChange={(v) => updateStep(step.id, { op: v })}>
                  <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event_exists">Event exists</SelectItem>
                    <SelectItem value="event_absent">Event absent</SelectItem>
                    <SelectItem value="event_count">Event count</SelectItem>
                    <SelectItem value="action">Trigger action</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeStep(step.id)} className="text-destructive h-7 text-xs">Remove</Button>
            </div>

            {step.op !== "action" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Event">
                    <Select value={step.event} onValueChange={(v) => updateStep(step.id, { event: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{formatEventType(t)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Within (time window)">
                    <Input value={step.within || ""} onChange={(e) => updateStep(step.id, { within: e.target.value })} placeholder="e.g. 30 days" />
                  </Field>
                  {step.op === "event_count" && (
                    <Field label="Count">
                      <Input value={step.count || ""} onChange={(e) => updateStep(step.id, { count: e.target.value })} placeholder=">= 3" className="font-mono" />
                    </Field>
                  )}
                </div>
                {step.op !== "event_count" && (
                  <ExpressionBuilder
                    label="Advanced condition (optional)"
                    value={step.conditionExpr || ""}
                    onChange={(v) => updateStep(step.id, { conditionExpr: v })}
                    rows={3}
                    testId={`step-${idx}-expr`}
                    placeholder="{{ event.event_source == 'web' && event.event_datetime > '2026-01-01' }}"
                  />
                )}
              </div>
            )}

            {step.op === "action" && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Action name">
                    <Input value={step.action || ""} onChange={(e) => updateStep(step.id, { action: e.target.value })} className="font-mono text-xs" />
                  </Field>
                  <Field label="Target">
                    <Select value={step.target || "customer"} onValueChange={(v) => updateStep(step.id, { target: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="account">Account</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Webhook">
                    <Button variant="outline" onClick={() => setWebhookStepId(step.id)} data-testid={`step-${idx}-webhook`} className="h-9 w-full gap-2 justify-start font-mono text-xs">
                      <Settings2 className="w-3.5 h-3.5 text-primary" />
                      <span className="truncate">{step.webhook?.method || "POST"} · {(step.webhook?.url || "").replace("https://", "").slice(0, 30) || "configure…"}</span>
                    </Button>
                  </Field>
                </div>
                <div className="text-xs text-muted-foreground border border-border rounded-md p-2.5 bg-background/40 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <span>The action fires an HTTP webhook with the full detection context (headers, body, auth). Configure it in the modal.</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={() => addStep("event_exists")} data-testid="add-step-exists" className="gap-1"><Plus className="w-3 h-3" /> Exists</Button>
        <Button variant="outline" size="sm" onClick={() => addStep("event_absent")} data-testid="add-step-absent" className="gap-1"><Plus className="w-3 h-3" /> Absent</Button>
        <Button variant="outline" size="sm" onClick={() => addStep("event_count")} data-testid="add-step-count" className="gap-1"><Plus className="w-3 h-3" /> Count</Button>
        <Button variant="outline" size="sm" onClick={() => addStep("action")} data-testid="add-step-action" className="gap-1"><Plus className="w-3 h-3" /> Action</Button>
      </div>

      <NodeConfigDialog
        open={!!webhookStep}
        onOpenChange={(v) => !v && setWebhookStepId(null)}
        node={webhookNode}
        onSave={(data) => {
          updateStep(webhookStepId, { webhook: data, action: data.label || webhookStep?.action });
        }}
        mode="detection"
      />
    </Card>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="label-caps">{label}</Label>
    {children}
  </div>
);

/* ================= AI Detection Tab ================= */


/* ============= AI Detection Tab (read-only, backend-managed) ============= */

const SEV_ROW = {
  high: "text-red-500 border-red-500/30 bg-red-500/10",
  medium: "text-[#B67F1B] dark:text-[#E9B44C] border-[#E9B44C]/40 bg-[#E9B44C]/10",
  low: "text-primary border-primary/30 bg-primary/10",
};

function AIDetectionTab() {
  const [selected, setSelected] = useState(AI_USECASES[0]);
  const totalSignals = AI_USECASES.reduce((s, e) => s + e.signals_captured, 0);
  const listening = AI_USECASES.filter((e) => e.status === "listening").length;

  return (
    <div className="space-y-5">
      {/* Header stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile icon={Radio} label="Active listeners" value={listening} sub={`of ${AI_USECASES.length} deployed`} testid="ai-stat-active" glow />
        <StatTile icon={Rss} label="Signals captured" value={totalSignals.toLocaleString()} sub="last 30 days" testid="ai-stat-signals" />
        <StatTile icon={Activity} label="Stream ingest" value="3,842/min" sub="events per minute" testid="ai-stat-ingest" />
        <StatTile icon={BrainCircuit} label="Model" value="detect-v4.1" sub="patched 2 days ago" testid="ai-stat-model" />
      </div>

      <div className="rounded-md border border-border bg-secondary/40 px-4 py-3 flex items-start gap-3 text-xs">
        <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <div className="text-muted-foreground leading-relaxed">
          AI detection engines are provisioned server-side by the OptiKurve platform team. This page is a read-only view of what is currently listening on the event stream, the signals each has captured, and the operational history.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Use-case list */}
        <div className="lg:col-span-1 space-y-3">
          <div className="label-caps">Deployed use cases · {AI_USECASES.length}</div>
          {AI_USECASES.map((e) => (
            <Card
              key={e.id}
              data-testid={`ai-usecase-${e.id}`}
              onClick={() => setSelected(e)}
              className={cn(
                "p-4 border-border cursor-pointer transition-all",
                selected?.id === e.id ? "border-primary teal-glow" : "hover:border-primary/50"
              )}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-primary/60 animate-ping" />
                    <span className="relative rounded-full w-2 h-2 bg-primary" />
                  </span>
                  <span className="text-primary label-caps text-[0.6rem]">Listening</span>
                </span>
                <span className="label-caps text-[0.55rem]">{e.scope}</span>
              </div>
              <div className="font-display text-base font-semibold mb-1 line-clamp-1">{e.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{e.prompt}</div>
              <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border text-xs">
                <span className="font-mono">
                  <span className="text-primary">{e.signals_captured}</span>
                  <span className="text-muted-foreground"> signals</span>
                </span>
                <span className="text-muted-foreground">{e.last_signal_at}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6 border-border ai-border" data-testid={`ai-usecase-detail-${selected.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 gap-1.5">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inset-0 rounded-full bg-primary/60 animate-ping" />
                      <span className="relative rounded-full w-1.5 h-1.5 bg-primary" />
                    </span>
                    Listening live
                  </Badge>
                  <span className="label-caps text-[0.6rem]">Scope · {selected.scope}</span>
                </div>
                <div className="font-display text-2xl font-semibold">{selected.name}</div>
                <div className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
                  &ldquo;{selected.prompt}&rdquo;
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
              <div>
                <div className="label-caps">Signals captured</div>
                <div className="number-display text-2xl font-semibold text-primary">{selected.signals_captured}</div>
              </div>
              <div>
                <div className="label-caps">Last signal</div>
                <div className="text-sm font-medium mt-1">{selected.last_signal_at}</div>
              </div>
              <div>
                <div className="label-caps">Deployed by</div>
                <div className="text-sm font-mono mt-1 truncate">{selected.deployed_by}</div>
              </div>
            </div>
          </Card>

          {/* Captured signals */}
          <div>
            <div className="label-caps mb-2">Captured signals — {selected.linked_insights.length} insights</div>
            <div className="space-y-3">
              {selected.linked_insights.map((ins) => {
                const sev = SEV_ROW[ins.severity];
                return (
                  <Card key={ins.id} data-testid={`ai-insight-${ins.id}`} className="border-border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={cn("border capitalize text-[0.65rem]", sev)}>{ins.severity}</Badge>
                          <span className="label-caps text-[0.55rem]">confidence {Math.round(ins.confidence * 100)}%</span>
                        </div>
                        <div className="font-display text-base font-semibold leading-tight">{ins.title}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{ins.body}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="number-display text-2xl font-semibold text-primary">{ins.metric}</div>
                        <div className="label-caps text-[0.55rem] mt-0.5">{ins.metric_label}</div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* History */}
          <div>
            <div className="label-caps mb-2">Operational history — who did what</div>
            <Card className="border-border overflow-hidden">
              <ol className="divide-y divide-border" data-testid={`ai-history-${selected.id}`}>
                {selected.history.map((h, i) => (
                  <li key={i} className="px-4 py-3 flex items-start gap-4">
                    <div className="w-24 text-[0.7rem] text-muted-foreground font-mono shrink-0 mt-0.5">
                      {new Date(h.at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{h.action}</span>
                        <span className="text-[0.7rem] font-mono text-muted-foreground">by {h.who}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{h.detail}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatTile = ({ icon: Icon, label, value, sub, testid, glow }) => (
  <Card data-testid={testid} className={cn("p-4 border-border", glow && "ai-border")}>
    <div className="flex items-center justify-between mb-3">
      <div className="label-caps">{label}</div>
      <div className={cn("w-8 h-8 rounded-md flex items-center justify-center", glow ? "bg-primary/10 border border-primary/30 text-primary" : "bg-secondary text-muted-foreground")}>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      </div>
    </div>
    <div className="number-display text-2xl font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{sub}</div>
  </Card>
);
